import { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, Table, Thead, Tbody, Tr, Th, Td, Select,
  Badge, useToast, Spinner, Flex, Input, HStack, Stack, Divider, useBreakpointValue,
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';

const ROLE_COLORS = {
  admin: 'red',
  pasteur: 'purple',
  president: 'pink',
  commissaire: 'cyan',
  tresorier: 'blue',
  secretaire: 'teal',
  editeur: 'orange',
  membre: 'gray',
};

export default function RoleManagement() {
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rolesData, usersData] = await Promise.all([
        apiService.makeRequest('/roles'),
        apiService.makeRequest('/roles/users'),
      ]);
      setRoles(rolesData.roles || []);
      setUsers(usersData.users || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      setSavingId(userId);
      await apiService.makeRequest(`/roles/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast({ title: 'Rôle mis à jour', status: 'success', duration: 3000 });
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSavingId(null);
    }
  };

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    return !q || `${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(q);
  });

  return (
    <Box maxW="1000px" mx="auto">
      <Heading size="lg" mb={2}>Rôles & Permissions</Heading>
      <Text color="gray.500" mb={6}>Attribuez un rôle à chaque membre pour lui donner des droits spécifiques.</Text>

      <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={6}>
        <Text fontWeight="bold" mb={3}>Rôles disponibles</Text>
        <HStack spacing={3} flexWrap="wrap">
          {roles.map(r => (
            <Badge key={r.value} colorScheme={ROLE_COLORS[r.value] || 'gray'} p={2} borderRadius="md" whiteSpace="normal" textTransform="none" maxW="100%">
              {r.label} — {r.description}
            </Badge>
          ))}
        </HStack>
      </Box>

      <Input
        placeholder="Rechercher un membre..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        mb={4}
        bg="white"
        maxW="350px"
      />

      {loading ? (
        <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
      ) : isMobile ? (
        <Box bg="white" borderRadius="md" shadow="sm" p={4}>
          <Stack spacing={3} divider={<Divider />}>
            {filteredUsers.map(u => (
              <Box key={u.id}>
                <HStack justify="space-between" align="flex-start">
                  <Text fontWeight="semibold">{u.prenom} {u.nom}</Text>
                  <Badge colorScheme={ROLE_COLORS[u.role] || 'gray'}>{u.role}</Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600" mb={2} wordBreak="break-all">{u.email}</Text>
                <Select
                  size="sm"
                  value={u.role}
                  aria-label={'Rôle de ' + u.prenom + ' ' + u.nom}
                  isDisabled={savingId === u.id}
                  onChange={e => handleRoleChange(u.id, e.target.value)}
                >
                  {roles.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </Select>
              </Box>
            ))}
          </Stack>
          {filteredUsers.length === 0 && (
            <Text textAlign="center" color="gray.500" py={6}>Aucun membre trouvé</Text>
          )}
        </Box>
      ) : (
        <Box bg="white" borderRadius="md" shadow="sm" overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr><Th>Membre</Th><Th>Email</Th><Th>Rôle actuel</Th><Th>Modifier</Th></Tr>
            </Thead>
            <Tbody>
              {filteredUsers.map(u => (
                <Tr key={u.id}>
                  <Td>{u.prenom} {u.nom}</Td>
                  <Td fontSize="sm" color="gray.600">{u.email}</Td>
                  <Td>
                    <Badge colorScheme={ROLE_COLORS[u.role] || 'gray'}>{u.role}</Badge>
                  </Td>
                  <Td>
                    <Select
                      size="sm"
                      value={u.role}
                      maxW="200px"
                      isDisabled={savingId === u.id}
                      onChange={e => handleRoleChange(u.id, e.target.value)}
                    >
                      {roles.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {filteredUsers.length === 0 && (
            <Text textAlign="center" color="gray.500" py={6}>Aucun membre trouvé</Text>
          )}
        </Box>
      )}
    </Box>
  );
}
